import {AxisTitleOptions, AxisTypeValue, XAxisLabelsOptions, YAxisLabelsOptions} from 'highcharts';

import {PropertyType} from 'common/report/constants';
import {BaseDate, DateRangeType, ExclusionOffset, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {Behavior, CustomProperty, ProfileHistoryOptions, Segmentation} from 'common/types/reports/query';
import {BaseQueryErrorType, BaseQueryResultsType, ForecastValue} from 'common/report/types';
import {Bucket, StringBucket} from 'common/widgets/bucket-pane/types';
import type {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {SerializedBehaviorDictionary} from 'common/query-builder/cohorts-query-builder/models/types/serialized-cohort-types';
import {Expression} from 'common/query-builder/cohorts-query-builder/models/types/expression-types';
import type {BookmarkTheme} from 'common/report/queries/app-api/themes';
import {FilterValue, ListQuantifier} from 'common/widgets/property-filter-menu/models/types';
import {TranslationKey} from 'common/i18n';
import * as BookmarkTypes from 'common/types/reports/bookmark';

export type FilterDeterminer = `all` | `any`;

export interface FlatSegment<ValueT = number> {
  key: string;
  path: string[];
  displayName: string;
  displayNamePath?: string[];
  // object key is an ISO-8601 date string for time data
  // or a single arbitrary leaf key for series data e.g. `all`, `value`, `null`
  value: Record<string, ValueT>;
  secondaryValue?: Record<string, ValueT>;
}

export enum TypeOfComparison {
  Time = `time_comparison`,
  Lift = `lift_comparison`,
}

export const AnalysisTypeLabelKey: {[key in BookmarkTypes.AnalysisType]: TranslationKey} = {
  [BookmarkTypes.AnalysisType.Linear]: `reports.analysis-types.linear`,
  [BookmarkTypes.AnalysisType.Logarithmic]: `reports.analysis-types.logarithmic`,
  [BookmarkTypes.AnalysisType.Rolling]: `reports.analysis-types.rolling`,
  [BookmarkTypes.AnalysisType.Cumulative]: `reports.analysis-types.cumulative`,
};

export type AxisUnitOptions = Pick<BookmarkTypes.MetricDisplay, `abbrev` | `prefix` | `suffix` | `precision`>;

// Maximum precision is 8 decimal places
export type AxisUnitPrecision = BookmarkTypes.MetricDisplay[`precision`];
export type BookmarkYAxisOptions = {
  axisUnit?: AxisUnitOptions;
  labels?: Partial<YAxisLabelsOptions>;
  min?: number;
  max?: number;
  title?: Partial<AxisTitleOptions> & {placeholder?: string};
  type?: AxisTypeValue;
  useSoftMinMax?: boolean;
};

export type BookmarkXAxisOptions = {
  title?: Partial<AxisTitleOptions> & {placeholder?: string};
  labels?: Partial<XAxisLabelsOptions>;
  hideWeekends?: boolean;
};

/**
 * DisplayOptions is part of the shared bookmark schema, but the definitions
 * for some of the fields come from 3rd party JS libraries, so we generate the
 * GeneratedDisplayOptions type to allow any type for those fields and then override
 * them here to the correct type.
 *
 * If making updates to any field in DisplayOptions not overridden below, make the
 * change in the shared schema at lib/common/mxpnl/report/bookmarks/insights/display_options.py
 */
export interface DisplayOptions
  extends Omit<
    BookmarkTypes.DisplayOptions,
    `primaryYAxisOptions` | `secondaryYAxisOptions` | `theme` | `xAxisOptions`
  > {
  primaryYAxisOptions?: BookmarkYAxisOptions;
  secondaryYAxisOptions?: BookmarkYAxisOptions;
  theme?: BookmarkTheme;
  xAxisOptions?: BookmarkXAxisOptions;
}

export type InsightsSortConfig = BookmarkTypes.InsightsBookmarkSortConfig & {
  [BookmarkTypes.ChartType.Line]: BookmarkTypes.SortConfig;
};

export type MetricBehavior = BookmarkTypes.Behavior;

export type MetricMeasurementProperty = {
  dataset: string;
  name: string | string[];
  renamed?: string;
  custom?: boolean;
  id?: number;
  type: PropertyType;
  defaultType: PropertyType;
  // seems like sunset feature related
  is_collect_everything_event?: boolean;
  resourceType?: BookmarkTypes.InsightsResourceType;
  raw_cohort?: TODO;
  customPropertyId?: string | number;
  customProperty?: CustomProperty;
  joinPropertyType?: PropertyType;
  tempDataGroupId?: string;
  dataGroupId?: string;
  propertyObjectKey?: string | string[];
  scdEvent?: string;
  behavior?: Segmentation[`behavior`];
  scdOffset?: Behavior[`offset`];

  /** Used to specify a unit within a date property. For example, when segmenting on a date */
  unit?: TimeUnit;

  listItemGroup?: Segmentation;
};

export type MetricMeasurement = BookmarkTypes.BehaviorMeasurement;

// New format which show clause will transform into
export type BehaviorMetric = BookmarkTypes.BehaviorShowClause;

/**
 * By default we abbreviate units like "10000" -> "10k"
 */
export const BEHAVIOR_METRIC_ABBREVIATION_DEFAULT = true;

export type BehaviorMetricDisplay = BookmarkTypes.MetricDisplay;

/**
 * FormulaMetric is used to represent 3 different types of formulas. Here's how to check for each type:
 * 1. inline formula: `id` is not set
 * 2. saved formula: only `id` is set
 * 3. saved formula with modifications: `name`, `definition`, `referencedMetrics` are set
 */
export type ShowClauseFormula = BookmarkTypes.FormulaShowClause;

export type FormulaReferencedMetrics = BookmarkTypes.BehaviorShowClause[];

export type FormulaMeasurement = BookmarkTypes.FormulaMeasurement;

export type FormulaShowClause = BookmarkTypes.FormulaShowClause;

export type BehaviorShowClause = BookmarkTypes.BehaviorShowClause;
export type ShowClause = BookmarkTypes.BehaviorShowClause | BookmarkTypes.FormulaShowClause;

export interface GroupByCohort {
  dataset?: any;
  id?: string | number | null;
  name?: string | null;
  negated?: boolean | null;
  raw_cohort?: {
    unsavedId?: string;
    behaviors: SerializedBehaviorDictionary;
    selector: Expression;
    data_group_id?: string;
  };
}

/**
 * @deprecated Use GroupClause from iron/common/types/reports/bookmark.ts instead.
 */
export interface GroupClause {
  behavior?: Behavior;
  cohorts?: GroupByCohort[] | null;
  customBucket?: Bucket | null;
  customGroups?: StringBucket;
  customProperty?: CustomProperty;
  customPropertyId?: number | string | null;
  dataGroupId?: string | null;
  dataset?: string | null;
  datasetId?: string | null;
  editing?: boolean | null;
  endStepNumber?: number | null;
  isHidden?: boolean | null;
  isInherited?: boolean | null;
  joinPropertyType?: PropertyType | null;
  listItemGroup?: GroupClause | null;
  listQuantifier?: ListQuantifier | null;
  profileHistoryOptions?: ProfileHistoryOptions | null;
  profileType?: string | null;
  projectId?: number | null;
  propertyObjectKey?: string | string[] | null;
  propertyType?: PropertyType | null;
  resourceType?: BookmarkTypes.InsightsResourceType | null;
  scdEvent?: string | null;
  scdOptions?: Segmentation[`scdOptions`] | null;
  experiments?: Segmentation[`experiments`] | null;
  search?: string | null;
  startStepNumber?: number | null;
  // when tempDataGroupId is set, it's pointing to the lookup table e.g. ['Distinct ID', 'Mapped Property']
  tempDataGroupId?: string | null;
  typeCast?: PropertyType | null;
  unit?: TimeUnit | null;
  value?: FilterValue | null;
}

export interface FormulaClause {
  value: string;
  label?: string;
}

export interface TimeClause {
  unit: TimeUnit;
  value?: number | string | [BaseDate] | [BaseDate, BaseDate];
  from_date?: BaseDate;
  to_date?: BaseDate;
  rolling?: boolean;
  window?: {
    unit: TimeUnit;
    value: number;
  };
  exclusionOffset?: ExclusionOffset | null;
  dateRangeType?: DateRangeType;
  dataset?: string;
  time_aggregation_unit?: TimeUnit;
  as_of_timestamp?: number;
  time_comparison?: TODO;
  lift_comparison?: TODO;
}

export interface FilterClause {
  behavior?: PropertyFilter[`behavior`];
  customProperty?: PropertyFilter[`customProperty`];
  customPropertyId?: PropertyFilter[`customPropertyId`] | null;
  dataGroupId?: PropertyFilter[`dataGroupId`] | null;
  dataset?: string | null;
  defaultType?: PropertyFilter[`propertyDefaultType`] | null;
  determiner?: FilterDeterminer;
  endStepNumber?: PropertyFilter[`endStepNumber`] | null;
  filterDateUnit?: PropertyFilter[`filterDateUnit`] | null;
  filterJoinType?: PropertyFilter[`joinPropertyType`] | null;
  filterOperator?: PropertyFilter[`filterOperator`] | null;
  filterType?: PropertyFilter[`propertyType`] | null;
  filterValue?: TODO; // it is not the same shape as PropertyFilter['filterValue']
  isHidden?: PropertyFilter[`isHidden`] | null;
  isInherited?: PropertyFilter[`isInherited`] | null;
  listInclusion?: PropertyFilter[`listInclusion`] | null;
  listItemFilters?: PropertyFilter[`listItemFilters`] | null;
  listQuantifier?: PropertyFilter[`listQuantifier`] | null;
  profileHistoryOptions?: ProfileHistoryOptions | null;
  profileType?: string | null;
  propertyObjectKey?: PropertyFilter[`propertyObjectKey`] | null;
  resourceType?: BookmarkTypes.InsightsResourceType | null;
  scdEvent?: PropertyFilter[`scdEvent`] | null;
  scdOptions?: PropertyFilter[`scdOptions`] | null;
  experiments?: PropertyFilter[`experiments`] | null;
  search?: string | null;
  startStepNumber?: PropertyFilter[`startStepNumber`] | null;
  tempDataGroupId?: PropertyFilter[`tempDataGroupId`] | null; // when tempDataGroupId is set, it's pointing to the lookup table e.g. ['Distinct ID', 'Mapped Property']
  value?: FilterValue | null;
}

export function isInsightsBookmarkParams(params: any): params is BookmarkTypes.InsightsBookmarkParams {
  return params && params.sections && params.sections.show;
}
export type InsightsChartParams = Pick<
  BookmarkTypes.InsightsBookmarkParams,
  `sections` | `displayOptions` | `liftComparison`
>;

export type InsightsSeriesTree<ValueT = number> =
  | {[key: string]: ValueT}
  | {[key: string]: ValueT | InsightsSeriesTree<ValueT>};

interface DateRange {
  // sic; this is NOT BookmarkDateRange or a similar bookmark-related type
  from_date: string;
  to_date: string;
}

interface IncompleteBuckets {
  [date: string]: Array<null | string>;
}

export interface MetricMetadata {
  metric_key?: string;
  sub_queries?: Array<{apiResultsTruncated?: boolean; metric_key: string}>;
  // funnels specific
  is_event_comparison?: boolean;
  // retention specific meta data
  retention_buckets_size?: number;
  incomplete_length?: number;
  incomplete_buckets?: IncompleteBuckets;
  past_incomplete_buckets?: IncompleteBuckets;
  /*
    Augmented on the client-side by queries/insights based on query limits and segments in the result
  */
  apiResultsTruncated?: boolean;
}

export interface ReportSectionsMetadata {
  cohorts?: Array<{bookmark: GroupByCohort}>;
  group?: Array<{bookmark: GroupClause; numeric_buckets?: Bucket}>;
  show?: MetricMetadata[];
  formula?: MetricMetadata[];
}

export type ExperimentMetricResultsFormat = `number` | `percent`;

export type StatsigResults = {
  [variantKey: string]: {
    all?: number;
    count: number;
    exposures: number;
    format: ExperimentMetricResultsFormat;
    is_significant: `True` | `False`;
    lift: number;
    lift_margin_of_error: number;
    p_value: number;
    raw_data?: number;
    sample_size?: number;
    value: number;
    var: number;
    // Bayesian-only fields (see api/version_2_0/insights/bayesian.py). Present only when
    // the metric query ran with statsig.model = "bayesian".
    prob_beat_control?: number;
    expected_loss?: number;
    credible_interval_low?: number;
    credible_interval_high?: number;
  };
};

export type StatsigSeriesResults = {
  [seriesName: string]: StatsigResults;
};

/**
 * Type guard to check if a series object is StatsigSeriesResults (experiment data)
 * rather than a regular InsightsSeriesTree.
 */
export interface SegmentationQueryLimits {
  limit?: number;
}

export interface InsightsQueryError extends BaseQueryErrorType {
  formula_errors?: Array<{index: number; message: string}>;
}

// Mirror of the entity `type` values produced by `enforce_verified_ai_mode`
// in api/version_2_0/verified_ai_mode.py. Keep in sync.
export type GovernanceFilteredEntityType = `event` | `custom-event` | `cohort` | `behavior` | `metric` | `formula`;

// During deploy skew the API may emit either value (see api/version_2_0/api.py):
// query-api emits `governed_ai_mode` until its rename PR lands, then `verified_ai_mode`.
// Mirrors the `verified_ai_mode ?? governed_ai_mode` fallback used for project metadata.
// TODO: narrow to `verified_ai_mode` in the cleanup PR once query-api has deployed.
export type GovernanceMode = `governed_ai_mode` | `verified_ai_mode`;

export interface GovernanceFilteredEntity {
  name: string;
  type: GovernanceFilteredEntityType;
  // Free-form: usually the literal `unverified`, but formula entries include
  // a list of dependent filtered entities.
  reason: string;
}

export interface GovernanceMeta {
  filtered_entities: GovernanceFilteredEntity[];
  mode: GovernanceMode;
  filtered_params?: {bookmark?: BookmarkTypes.InsightsBookmarkParams};
  fully_filtered?: boolean;
  // Total entities considered for filtering (filtered + still-allowed). Lets
  // satisfaction analysis compute a filter rate (filtered_count / total_entities).
  total_entities?: number;
}

export interface InsightsQueryResults<ValueT = number> extends BaseQueryResultsType {
  date_range: DateRange;
  headers: string[];
  meta: {
    min_sampling_factor: number;
    report_sections: ReportSectionsMetadata;
    /*
    Augmented on the client-side to carry the limits used on the query that generated these results.
    When limits affect the results, we need to tell the user where the cutoff is.
    */
    queryLimits?: SegmentationQueryLimits;
    apiResultsTruncated?: boolean;
    liftComparison?: any;
    time_period_settings?: {quarter_start_month: number};
    is_segmentation_limit_hit?: boolean;
    governance?: GovernanceMeta;
  };
  series: InsightsSeriesTree<ValueT>;
  forecast_comparison?: {
    series: InsightsSeriesTree<ForecastValue>;

    // these properties are intended for manual debugging and shouldn't be referenced in non-experimental code, they will likely be removed after GA
    date_range: {
      historical_from_date: string;
      historical_to_date: string;
    };
    historical_series: InsightsSeriesTree;
  };
  secondary?: {
    series: InsightsSeriesTree<ValueT>;
  };
  secondary_time_comparison?: {
    series: InsightsSeriesTree<ValueT>;
  };
  time_comparison?: {
    date_range: DateRange;
    series: InsightsSeriesTree<ValueT>;
  };
  lift_comparison?: {
    series: InsightsSeriesTree<ValueT>;
  };
  /**
   * Full statistical data for experiment comparisons.
   * Contains per-variant, per-date statsig results including lift, p_value, is_significant, etc.
   * Only present when liftComparison.type === 'experiment'.
   */
  statsig?: {
    [metricKey: string]: StatsigSeriesResults;
  };
}

export interface AggregatedSeriesValue {
  // list of all possible paths to reach the segment in this series
  paths: string[][];

  // sum of all of this segments values in this series
  total: number;
}

export interface AggregatedSeries {
  [label: string]: AggregatedSeriesValue;
}
